import React, { useMemo } from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";

import {
  describeTimeZone,
  timeZoneLabel,
  timeZoneOptions,
  timeZoneSearchText,
} from "@/utils/temporal";

/**
 * Choosing a timezone, once, for everywhere that asks (#11055).
 *
 * Three surfaces ask -- the viewer's own zone in the profile menu, an
 * interaction's zone in the RMS editor, and the organization's zone in the
 * admin org editor -- and they had grown three answers, the last of them a
 * native `<select>`. That is the same defect this whole change exists to
 * remove, one level up: a question answered independently in several places
 * gets answered differently, and the differences are invisible until someone
 * hits one. A condensed list does not rescue a control you cannot type into
 * either, because 164 rows is still far past what anyone scrolls.
 *
 * So the behaviour lives here and the surfaces differ only in what they call
 * the control and what they do with the answer:
 *
 * - **A row is a zone, and it is named.** `(UTC-08:00) Pacific Time`, from
 *   `timeZoneOptions`, which offers each zone once rather than once per IANA
 *   name that describes it. What is stored, sent and compared is still the IANA
 *   name -- see `describeTimeZone` for why that separation is not negotiable.
 * - **Search matches every name a row stands for.** Naming zones in words takes
 *   `America/Los_Angeles` off the screen and condensing the list takes
 *   `America/Toronto` off it; neither should come out of the search box.
 * - **A zone already held stays selectable even when the list stops offering
 *   it.** Condensing what is *offered* must never narrow what is *accepted*, so
 *   a value that arrives under any of a zone's other names -- or under a name
 *   that outlived the tz database entry describing it -- gets a row of its own
 *   rather than reading as unset and being written away on the next save.
 */

/** One row of the picker: what a reader sees, what gets stored. */
interface ZoneRow {
  /** The IANA name, or `null` for the row that states no zone. */
  readonly zone: string | null;
  /** What the reader sees. Never what gets stored. */
  readonly label: string;
  /** The label without its offset, for the compact row that sets it apart. */
  readonly title: string;
  /** `UTC-08:00`, or `null` for an unset row that stands for no zone. */
  readonly offset: string | null;
  /** A quiet tag beside the title in the compact list, never in the field. */
  readonly hint: string | null;
  /** The IANA names this row stands for. Searchable, never stored. */
  readonly members: readonly string[];
}

/** A label is `(UTC-08:00) Pacific Time`; the compact row sets the offset
 *  apart, so it shows what follows it. */
const withoutOffset = (label: string, offset: string): string => {
  const prefix = `(${offset}) `;
  return label.startsWith(prefix) ? label.slice(prefix.length) : label;
};

/**
 * Search matches the label *and* the IANA names behind it, spelled both as
 * they are written and as they are read -- nobody types the underscore.
 */
const filterZoneRows = createFilterOptions<ZoneRow>({
  stringify: timeZoneSearchText,
});

/** React needs a key for the row that has no zone to be keyed by. */
const UNSET_ROW_KEY = "unset";

/** The form field: a white box on a form. */
const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: 14,
    borderRadius: "0.375rem",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#10141B" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#3b82f6",
    },
  },
  "& .MuiAutocomplete-popupIndicator": { cursor: "pointer" },
};

/** Inside a menu the field is no white box (Gerry: "the box shouldn't be
 *  white"): it sits on the menu's ground with a hairline, hovers as the
 *  menu's rows do, and focuses in ink rather than blue. */
const COMPACT_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: 13,
    color: "var(--fp-ink)",
    borderRadius: "5px",
    backgroundColor: "transparent",
    transition: "background-color 120ms",
    "&:hover": { backgroundColor: "var(--fp-hover)" },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--fp-border)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--fp-border)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--fp-border-strong)",
      borderWidth: "1px",
    },
  },
  "& .MuiAutocomplete-popupIndicator": {
    cursor: "pointer",
    color: "var(--fp-ink-3)",
  },
};

/** The list wears the account menu's paper: its surface, border, radius and
 *  lift, rows at the menu's type and height, one line each. */
const COMPACT_PAPER_SX = {
  marginTop: "4px",
  borderRadius: "7px",
  border: "1px solid var(--fp-border)",
  backgroundColor: "var(--fp-surface)",
  boxShadow: "var(--fp-lift)",
  backgroundImage: "none",
  "& .MuiAutocomplete-listbox": {
    padding: "4px",
    maxHeight: 264,
    fontSize: 13,
    color: "var(--fp-ink)",
  },
  "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
    minHeight: 30,
    gap: "8px",
    padding: "0 8px",
    borderRadius: "5px",
    whiteSpace: "nowrap",
  },
  "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
    backgroundColor: "var(--fp-hover)",
  },
  "& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected='true'], & .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected='true'].Mui-focused":
    {
      backgroundColor: "var(--fp-hover)",
      fontWeight: 500,
    },
  "& .MuiAutocomplete-noOptions": { fontSize: 13, color: "var(--fp-ink-3)" },
};

export interface TimeZonePickerProps {
  /**
   * The IANA name currently held, or `null` when nothing is chosen.
   *
   * `null` is only meaningful alongside `unsetLabel`: a surface that requires a
   * zone passes one and never sees the callback hand back `null`.
   */
  readonly value: string | null;
  /** The IANA name picked, or `null` for the unset row. Never a label. */
  readonly onChange: (zone: string | null) => void;
  /** What this control is called, for anyone not reading it with their eyes. */
  readonly ariaLabel: string;
  /**
   * The leading row meaning "no zone of its own", named by the surface because
   * only it knows what happens then -- following the browser, or falling back
   * to a default. Omitted where a zone is required.
   */
  readonly unsetLabel?: string;
  /** A zone the unset row should also answer to in the search box. */
  readonly unsetSearchZone?: string;
  /** A quiet tag on the unset row in the compact list ("where you are now"),
   *  kept out of the field so the field holds the zone's name alone. */
  readonly unsetHint?: string;
  /** Ties the control to a `<label htmlFor>` where the surface has one. */
  readonly inputId?: string;
  readonly disabled?: boolean;
  /** For a picker sitting inside a menu rather than a form: the menu's type
   *  and ground, one line per zone with its offset set apart, and the field
   *  holding the zone's name without the offset. */
  readonly compact?: boolean;
}

const TimeZonePicker: React.FC<TimeZonePickerProps> = ({
  value,
  onChange,
  ariaLabel,
  unsetLabel,
  unsetSearchZone,
  unsetHint,
  inputId,
  disabled = false,
  compact = false,
}) => {
  // Built on mount rather than at module load: naming four hundred zones costs
  // a few hundred `Intl` formatters, which is nothing next to opening a menu
  // and a waste on every page that never opens one.
  const rows: ZoneRow[] = useMemo(() => {
    const offered: ZoneRow[] = timeZoneOptions().map(
      ({ zone, label, offsetLabel, members }) => ({
        zone,
        label,
        title: withoutOffset(label, offsetLabel),
        offset: offsetLabel,
        hint: null,
        members,
      })
    );
    const heldZone = value !== null ? describeTimeZone(value) : null;
    const held: ZoneRow[] =
      value !== null && !offered.some((row) => row.zone === value)
        ? [
            {
              zone: value,
              label: timeZoneLabel(value),
              title: heldZone?.displayName ?? value,
              offset: heldZone?.offsetLabel ?? null,
              hint: null,
              members: [value],
            },
          ]
        : [];
    const unset: ZoneRow[] =
      unsetLabel === undefined
        ? []
        : [
            {
              zone: null,
              label: unsetLabel,
              title: unsetLabel,
              offset: unsetSearchZone
                ? (describeTimeZone(unsetSearchZone)?.offsetLabel ?? null)
                : null,
              hint: unsetHint ?? null,
              members: unsetSearchZone ? [unsetSearchZone] : [],
            },
          ];
    return [...unset, ...held, ...offered];
  }, [value, unsetLabel, unsetSearchZone, unsetHint]);

  const selected = rows.find((row) => row.zone === value) ?? rows[0];

  return (
    <Autocomplete
      options={rows}
      value={selected}
      disabled={disabled}
      disableClearable
      autoHighlight
      openOnFocus
      size="small"
      isOptionEqualToValue={(option, current) => option.zone === current.zone}
      filterOptions={filterZoneRows}
      getOptionLabel={(option) => (compact ? option.title : option.label)}
      onChange={(_event, picked) => onChange(picked.zone)}
      renderOption={(props, option) =>
        compact ? (
          <li {...props} key={option.zone ?? UNSET_ROW_KEY}>
            {/* The hint takes a line of its own: beside the title it is
                the first thing the menu's width cuts off. */}
            <span className={`min-w-0 flex-1 ${option.hint ? "py-1" : ""}`}>
              <span className="block truncate">{option.title}</span>
              {option.hint && (
                <span className="block truncate text-[11px] leading-tight text-[var(--fp-ink-3)]">
                  {option.hint}
                </span>
              )}
            </span>
            {option.offset && (
              <span className="flex-none font-mono text-[11px] text-[var(--fp-ink-3)]">
                {option.offset}
              </span>
            )}
          </li>
        ) : (
          <li {...props} key={option.zone ?? UNSET_ROW_KEY}>
            {option.label}
          </li>
        )
      }
      slotProps={compact ? { paper: { sx: COMPACT_PAPER_SX } } : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search time zones"
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              ...(inputId === undefined ? {} : { id: inputId }),
              "aria-label": ariaLabel,
            },
          }}
          sx={compact ? COMPACT_FIELD_SX : FIELD_SX}
        />
      )}
    />
  );
};

export default TimeZonePicker;
